import React, { useMemo } from 'react';
import { ChevronRight, Folder as FolderIcon, FolderOpen, HardDrive } from 'lucide-react';
import type { FileFolder } from '../types';

export interface TreeNode {
  folder: FileFolder | null;
  children: TreeNode[];
}

export const buildFolderTree = (folders: FileFolder[]): TreeNode => {
  const byParent = new Map<string | null, FileFolder[]>();
  for (const folder of folders) {
    const key = folder.parent ?? null;
    const list = byParent.get(key) ?? [];
    list.push(folder);
    byParent.set(key, list);
  }
  for (const [, list] of byParent) {
    list.sort((a, b) => a.name.localeCompare(b.name));
  }

  const build = (parentId: string | null): TreeNode[] => {
    const directChildren = byParent.get(parentId) ?? [];
    return directChildren.map((folder) => ({
      folder,
      children: build(folder.id),
    }));
  };

  return {
    folder: null,
    children: build(null),
  };
};

interface FileTreeProps {
  folders: FileFolder[];
  activeFolderId: string | null;
  expandedIds: Set<string>;
  onToggle: (id: string) => void;
  onExpand: (id: string) => void;
  onSelect: (folderId: string | null) => void;
  isLoading?: boolean;
}

export const FileTree: React.FC<FileTreeProps> = ({
  folders,
  activeFolderId,
  expandedIds,
  onToggle,
  onExpand,
  onSelect,
  isLoading = false,
}) => {
  const tree = useMemo(() => buildFolderTree(folders), [folders]);

  return (
    <div className="text-sm select-none">
      <RootRow
        active={activeFolderId === null}
        onClick={() => onSelect(null)}
      />
      {isLoading ? (
        <p className="px-2 py-2 text-xs text-muted-foreground">Loading...</p>
      ) : (
        <ul className="space-y-px">
          {tree.children.map((node) => (
            <TreeNodeRow
              key={node.folder!.id}
              node={node}
              depth={1}
              activeFolderId={activeFolderId}
              expandedIds={expandedIds}
              onToggle={onToggle}
              onExpand={onExpand}
              onSelect={onSelect}
            />
          ))}
        </ul>
      )}
    </div>
  );
};

interface RootRowProps {
  active: boolean;
  onClick: () => void;
}

const RootRow: React.FC<RootRowProps> = ({ active, onClick }) => (
  <button
    type="button"
    onClick={onClick}
    className={`flex w-full items-center gap-2 rounded-md px-2 py-1.5 text-left transition-colors ${
      active
        ? 'bg-accent text-accent-foreground font-medium'
        : 'text-foreground hover:bg-accent/60'
    }`}
  >
    <HardDrive className="h-4 w-4 text-muted-foreground" />
    <span>All Files</span>
  </button>
);

interface TreeNodeRowProps {
  node: TreeNode;
  depth: number;
  activeFolderId: string | null;
  expandedIds: Set<string>;
  onToggle: (id: string) => void;
  onExpand: (id: string) => void;
  onSelect: (id: string | null) => void;
}

const TreeNodeRow: React.FC<TreeNodeRowProps> = ({
  node,
  depth,
  activeFolderId,
  expandedIds,
  onToggle,
  onExpand,
  onSelect,
}) => {
  const folder = node.folder!;
  const hasChildren = node.children.length > 0;
  const isExpanded = expandedIds.has(folder.id);
  const isActive = activeFolderId === folder.id;

  return (
    <li>
      <div
        className={`flex items-center gap-1 rounded-md px-1 py-1 transition-colors ${
          isActive ? 'bg-accent text-accent-foreground font-medium' : 'hover:bg-accent/60'
        }`}
        style={{ paddingLeft: `${depth * 12}px` }}
      >
        <button
          type="button"
          aria-label={isExpanded ? 'Collapse folder' : 'Expand folder'}
          onClick={() => (hasChildren ? onToggle(folder.id) : undefined)}
          className={`flex h-5 w-5 items-center justify-center rounded transition-transform ${
            hasChildren ? 'text-muted-foreground hover:text-foreground' : 'invisible'
          } ${isExpanded ? 'rotate-90' : ''}`}
        >
          <ChevronRight className="h-3.5 w-3.5" />
        </button>
        <button
          type="button"
          onClick={() => {
            onSelect(folder.id);
            if (hasChildren) onExpand(folder.id);
          }}
          className="flex flex-1 items-center gap-2 truncate text-left"
        >
          {isExpanded && hasChildren ? (
            <FolderOpen className="h-4 w-4 text-primary shrink-0" />
          ) : (
            <FolderIcon className="h-4 w-4 text-primary shrink-0" />
          )}
          <span className="truncate">{folder.name}</span>
        </button>
      </div>
      {isExpanded && hasChildren && (
        <ul className="space-y-px">
          {node.children.map((child) => (
            <TreeNodeRow
              key={child.folder!.id}
              node={child}
              depth={depth + 1}
              activeFolderId={activeFolderId}
              expandedIds={expandedIds}
              onToggle={onToggle}
              onExpand={onExpand}
              onSelect={onSelect}
            />
          ))}
        </ul>
      )}
    </li>
  );
};
